import * as React from 'react'
import { DefaultContent, DisplayContent } from 'react-content-engine'
import { connect } from 'react-redux'

import { Button, TYPE } from 'components/Button'
import Heading from 'components/Heading'
import { List, ListItem } from 'components/List'
import ListHeader from 'components/ListHeader'
import GiftcardButton from '../buttons/GiftcardButton'

import IfCountry from '../conditionals/IfCountry'

import TotmExtrasSummary from '../../pages/totm/surfNBeach/summary/TotmExtrasSummary'
import TotmSummary from '../../pages/totm/surfNBeach/summary/TotmSummary'
import { getNameWithTheme, Slink } from '../../routing/links'
import DriveExtrasSummary from './DriveExtrasSummary'
import DriveSummary from './DriveSummary'
import FlyExtrasSummary from './FlyExtrasSummary'
import FlySummary from './FlySummary'
import SoloSummary from './SoloSummary'

import { getEntry, getEntryUrl } from '../../data/entries/entry'
import { EntryKey } from '../../data/entries/types'
import { IDiscountCode } from '../../data/models/funnelOrder/discountCode'
import {
  driveThemes,
  flyThemes,
  Theme,
} from '../../data/models/funnelOrder/theme'
import { SiteCountry, SiteCurrency } from '../../data/models/locale'
import { AppState } from '../../data/state'
import { formatMoney } from '../../utils/price'
import MultiDriveSummary from './MultiDriveSummary'
import SoloExtrasSummary from './SoloExtrasSummary'

interface SummaryProps {
  final?: boolean
  theme: Theme
  entry: EntryKey
  discountCode: IDiscountCode | null
  currency: SiteCurrency
  children?: React.ReactNode
  useWallet: boolean
  wallet: number
  hideGiftcardButton?: boolean
  fixedParams?: string[]
}

const Summary: React.FC<SummaryProps> = ({
  theme,
  entry,
  discountCode,
  currency,
  final,
  useWallet,
  wallet,
  hideGiftcardButton,
  fixedParams,
}) => {
  const entryValue = getEntry(entry)
  const page =
    entry !== EntryKey.none && entryValue ? entryValue.page : undefined

  return (
    <React.Fragment>
      <Heading level={5}>
        {final ? (
          <DisplayContent addNode contentKey="final-summary-heading">
            Before you continue, please check your details
          </DisplayContent>
        ) : (
          <DisplayContent contentKey="summary-heading">
            The options for your trip:
          </DisplayContent>
        )}
      </Heading>
      {final ? (
        <ListHeader
          button={
            <Slink
              name={
                entry !== EntryKey.none
                  ? 'entryPage'
                  : getNameWithTheme('customise')
              }
              params={{ code: page ? getEntryUrl(page) : undefined }}
            >
              <Button cutOff="none">
                <DefaultContent contentKey="edit">Edit</DefaultContent>
              </Button>
            </Slink>
          }
        />
      ) : (
        ''
      )}
      <List>
        {flyThemes.includes(theme) ? (
          <FlySummary fixedParams={fixedParams} />
        ) : (
          ''
        )}
        {[Theme.drive, Theme.train, Theme.foodLoversDrive].includes(theme) ? (
          <DriveSummary />
        ) : (
          ''
        )}
        {theme === Theme.multiDrive ? <MultiDriveSummary /> : ''}
        {theme === Theme.totm ? <TotmSummary /> : ''}
        {theme === Theme.solo ? <SoloSummary /> : ''}
      </List>

      <ListHeader
        button={
          final ? (
            <Slink name={getNameWithTheme('extras')}>
              <Button cutOff="none">
                <DefaultContent contentKey="edit">Edit</DefaultContent>
              </Button>
            </Slink>
          ) : (
            ''
          )
        }
      >
        <DisplayContent addNode contentKey="summary-extras">
          Extras
        </DisplayContent>
      </ListHeader>
      <List>
        {flyThemes.includes(theme) ? <FlyExtrasSummary /> : ''}
        {driveThemes.includes(theme) ? <DriveExtrasSummary /> : ''}
        {theme === Theme.totm ? <TotmExtrasSummary /> : ''}
        {theme === Theme.solo ? <SoloExtrasSummary /> : ''}
      </List>
      <ListHeader>
        <DisplayContent addNode contentKey="summary-other-things">
          Other things
        </DisplayContent>
      </ListHeader>
      <div className="margin-b-m">
        <List>
          <ListItem>
            <IfCountry country={SiteCountry.nl}>
              <DisplayContent addNode contentKey="summary-booking-calamity">
                Booking costs &amp; Calamity fund
              </DisplayContent>
            </IfCountry>
            <IfCountry
              country={[
                SiteCountry.be,
                SiteCountry.dk,
                SiteCountry.uk,
                SiteCountry.ie,
              ]}
            >
              <DisplayContent addNode contentKey="summary-booking-cost">
                Booking costs
              </DisplayContent>
            </IfCountry>
          </ListItem>
          {discountCode ? (
            <ListItem>
              {discountCode.isGift ? (
                <DisplayContent contentKey="discount-code-gift">
                  Free gift included
                </DisplayContent>
              ) : (
                <DisplayContent
                  contentKey="discount-code"
                  vars={{
                    code: discountCode.code,
                    amount: `${currency} ${discountCode.amount}`,
                  }}
                >
                  {`Discount code: ${currency} ${discountCode.amount}`}
                </DisplayContent>
              )}
            </ListItem>
          ) : null}
          {useWallet && wallet ? (
            <ListItem>
              <DisplayContent
                contentKey="wallet-used"
                vars={{
                  amount: formatMoney(wallet),
                }}
              >
                {`Used wallet: ${formatMoney(wallet)}`}
              </DisplayContent>
            </ListItem>
          ) : null}
        </List>
      </div>
      {hideGiftcardButton ? '' : <GiftcardButton />}
    </React.Fragment>
  )
}

export default connect((state: AppState) => ({
  discountCode: state.funnelOrder.discountCode,
  theme: state.funnelOrder.theme,
  entry: state.funnelOrder.entry,
  currency: state.locale.currency,
  useWallet: state.funnelOrder.useWallet,
  wallet: state.funnelOrder.wallet,
}))(Summary)
